import * as React from 'react';
const AccountPage = React.lazy(() =>
  import('@/components/account/account-page').then((module) => ({ default: module.AccountPage })),
);
const OperatorPortalPage = React.lazy(() =>
  import('@/components/account/operator-portal-page').then((module) => ({
    default: module.OperatorPortalPage,
  })),
);
import { PageSkeleton } from '@/components/admin/page-skeleton';
import { AppShell } from '@/components/app-shell';
const LoginPage = React.lazy(() =>
  import('@/components/login-page').then((module) => ({ default: module.LoginPage })),
);
import { Toaster } from '@/components/ui/toaster';
import { BrandingProvider } from '@/contexts/branding-context';
import { DealerTreeProvider } from '@/contexts/dealer-tree-context';
import { pageUsesDealerTree } from '@/lib/dealer-tree-pages';
import { isReactPublicRoute } from '@/lib/auth-public-routes';
import { initConsoleDiagnostics } from '@/lib/console-diagnostics';
import { adminPageRootClass } from '@/lib/admin-page-layout';
import { getReactAdminPage, type ReactAdminPageId } from '@/lib/react-admin-route-map';
import { UserAnalyticsProvider } from '@/components/user-analytics/user-analytics-provider';
import { DocumentTitle } from '@/components/document-title';

const ContactsAdminPage = React.lazy(() =>
  import('@/components/admin/contacts-admin-page-entry').then((module) => ({
    default: module.ContactsAdminPage,
  })),
);
const ReportsAdminPage = React.lazy(() =>
  import('@/components/reports/reports-admin-page').then((module) => ({ default: module.ReportsAdminPage })),
);
const SitesAdminPage = React.lazy(() =>
  import('@/components/admin/sites-admin-page-entry').then((module) => ({
    default: module.SitesAdminPage,
  })),
);
const OperatorNoticesPage = React.lazy(() =>
  import('@/components/operator-notices/operator-notices-page').then((module) => ({
    default: module.OperatorNoticesPage,
  })),
);
const ImportWizardPage = React.lazy(() =>
  import('@/components/import-wizard/import-wizard-page').then((module) => ({ default: module.ImportWizardPage })),
);
const OperatorSimulatorPage = React.lazy(() =>
  import('@/components/operator-simulator/operator-simulator-page').then((module) => ({
    default: module.OperatorSimulatorPage,
  })),
);
const SopsAdminPage = React.lazy(() =>
  import('@/components/sops-admin-workflow-page').then((module) => ({ default: module.SopsAdminWorkflowPage })),
);
const SystemTasksAdminPage = React.lazy(() =>
  import('@/components/system-tasks/system-tasks-admin-page').then((module) => ({
    default: module.SystemTasksAdminPage,
  })),
);
const DeliveryRulesAdminPage = React.lazy(() =>
  import('@/components/delivery-rules/delivery-rules-admin-page').then((module) => ({
    default: module.DeliveryRulesAdminPage,
  })),
);
const DeliveredReportsAdminPage = React.lazy(() =>
  import('@/components/delivered-reports/delivered-reports-admin-page').then((module) => ({
    default: module.DeliveredReportsAdminPage,
  })),
);
const OrchestratorAdminPage = React.lazy(() =>
  import('@/components/orchestrator-admin-page').then((module) => ({
    default: module.OrchestratorAdminPage,
  })),
);
const TemplatesAdminPage = React.lazy(() =>
  import('@/components/templates-admin-page').then((module) => ({ default: module.TemplatesAdminPage })),
);
const UsersAdminPage = React.lazy(() =>
  import('@/components/users-admin-page').then((module) => ({ default: module.UsersAdminPage })),
);
const OrchestratorWorkflowsAdminPage = React.lazy(() =>
  import('@/components/orchestrator-workflows-admin-page').then((module) => ({
    default: module.OrchestratorWorkflowsAdminPage,
  })),
);
const StatsAdminPage = React.lazy(() =>
  import('@/components/stats-admin-page').then((module) => ({ default: module.StatsAdminPage })),
);
const TechnologiesAdminPage = React.lazy(() =>
  import('@/components/technologies-admin-page').then((module) => ({ default: module.TechnologiesAdminPage })),
);
const RetentionHubAdminPage = React.lazy(() =>
  import('@/components/retention-hub-admin-page').then((module) => ({ default: module.RetentionHubAdminPage })),
);
const AiProfilesAdminPage = React.lazy(() =>
  import('@/components/ai-profiles-admin-page').then((module) => ({ default: module.AiProfilesAdminPage })),
);
const DealerPortalAdminPage = React.lazy(() =>
  import('@/components/dealer-portal-admin-page').then((module) => ({ default: module.DealerPortalAdminPage })),
);
const SettingsAdminPage = React.lazy(() =>
  import('@/components/settings-admin-page').then((module) => ({ default: module.SettingsAdminPage })),
);
const RolesAdminPage = React.lazy(() =>
  import('@/components/roles-admin-page').then((module) => ({ default: module.RolesAdminPage })),
);
const SystemLogsAdminPage = React.lazy(() =>
  import('@/components/system-logs-admin-page').then((module) => ({ default: module.SystemLogsAdminPage })),
);
const WebhookMonitorAdminPage = React.lazy(() =>
  import('@/components/webhook-monitor-admin-page').then((module) => ({ default: module.WebhookMonitorAdminPage })),
);
const UserAnalyticsPage = React.lazy(() =>
  import('@/components/user-analytics/user-analytics-page').then((module) => ({ default: module.UserAnalyticsPage })),
);
const ApplicationStatsAdminPage = React.lazy(() =>
  import('@/components/application-stats-admin-page').then((module) => ({
    default: module.ApplicationStatsAdminPage,
  })),
);
const NotificationsAdminPage = React.lazy(() =>
  import('@/components/notifications-admin-page').then((module) => ({
    default: module.NotificationsAdminPage,
  })),
);
const ToolsAdminPage = React.lazy(() =>
  import('@/components/tools/tools-admin-page').then((module) => ({
    default: module.ToolsAdminPage,
  })),
);
const BugReportsAdminPage = React.lazy(() =>
  import('@/components/bug-reports-admin-page').then((module) => ({
    default: module.BugReportsAdminPage,
  })),
);
const OperatorGradingConfigPage = React.lazy(() =>
  import('@/components/operator-grading-config-page').then((module) => ({
    default: module.OperatorGradingConfigPage,
  })),
);
const OperatorGradingReviewPage = React.lazy(() =>
  import('@/components/operator-grading-review-page').then((module) => ({
    default: module.OperatorGradingReviewPage,
  })),
);
const OperatorPerformancePage = React.lazy(() =>
  import('@/components/operator-performance-page').then((module) => ({
    default: module.OperatorPerformancePage,
  })),
);
const ReportVerificationPage = React.lazy(() =>
  import('@/components/report-verification/report-verification-page').then((module) => ({
    default: module.ReportVerificationPage,
  })),
);

// Module-level singleton: patch history.pushState/replaceState once at module load time.
// This avoids React.StrictMode double-mount issues (mount → cleanup → mount → unmount)
// where the first cleanup would restore originals and the second mount would re-patch.
// The patches are idempotent — each new call chains through the previous patch.
const _pathnames: Set<React.Dispatch<React.SetStateAction<string>>> = new Set();

function _notifyPathnameChange() {
  for (const setter of _pathnames) {
    setter(window.location.pathname);
  }
}

function _ensureHistoryPatched() {
  if ((history.pushState as any)._reactPatched) return;
  const originalPush = history.pushState;
  const originalReplace = history.replaceState;
  history.pushState = function (data: any, _: string, url?: string | null | undefined) {
    originalPush.call(history, data, _, url);
    _notifyPathnameChange();
  };
  (history.pushState as any)._reactPatched = true;
  history.replaceState = function (data: any, _: string, url?: string | null | undefined) {
    originalReplace.call(history, data, _, url);
    _notifyPathnameChange();
  };
  (history.replaceState as any)._reactPatched = true;
}

_ensureHistoryPatched();

function AdminPageContent({ pageId }: { pageId: ReactAdminPageId }) {
  return (
    <React.Suspense
      fallback={<PageSkeleton />}
    >
      {pageId === 'sites' ? <SitesAdminPage /> : null}
      {pageId === 'operator-notices' ? <OperatorNoticesPage /> : null}
      {pageId === 'wizard' ? <ImportWizardPage /> : null}
      {pageId === 'contacts' ? <ContactsAdminPage /> : null}
      {pageId === 'reports' ? <ReportsAdminPage /> : null}
      {pageId === 'operator-simulator' ? <OperatorSimulatorPage /> : null}
      {pageId === 'sops' ? <SopsAdminPage /> : null}
      {pageId === 'retention-hub' ? <RetentionHubAdminPage /> : null}
      {pageId === 'workflows' ? <OrchestratorWorkflowsAdminPage /> : null}
      {pageId === 'technologies' ? <TechnologiesAdminPage /> : null}
      {pageId === 'stats' ? <StatsAdminPage /> : null}
      {pageId === 'users' ? <UsersAdminPage /> : null}
      {pageId === 'templates' ? <TemplatesAdminPage /> : null}
      {pageId === 'system-tasks' ? <SystemTasksAdminPage /> : null}
      {pageId === 'system-logs' ? <SystemLogsAdminPage /> : null}
      {pageId === 'webhook-monitor' ? <WebhookMonitorAdminPage /> : null}
      {pageId === 'notifications' ? <NotificationsAdminPage /> : null}
      {pageId === 'user-analytics' ? <UserAnalyticsPage /> : null}
      {pageId === 'application-stats' ? <ApplicationStatsAdminPage /> : null}
      {pageId === 'ai-profiles' ? <AiProfilesAdminPage /> : null}
      {pageId === 'orchestrator' ? <OrchestratorAdminPage /> : null}
      {pageId === 'report-delivery' ? <DeliveryRulesAdminPage /> : null}
      {pageId === 'delivered-reports' ? <DeliveredReportsAdminPage /> : null}
      {pageId === 'roles' ? <RolesAdminPage /> : null}
      {pageId === 'settings' ? <SettingsAdminPage /> : null}
      {pageId === 'dealer-portal' ? <DealerPortalAdminPage /> : null}
      {pageId === 'account' ? (
        <AccountPage />
      ) : null}
      {pageId === 'operator-portal' ? (
        <OperatorPortalPage />
      ) : null}
      {pageId === 'tools' ? <ToolsAdminPage /> : null}
      {pageId === 'bug-reports' ? <BugReportsAdminPage /> : null}
      {pageId === 'operator-grading-config' ? <OperatorGradingConfigPage /> : null}
      {pageId === 'operator-grading-review' ? <OperatorGradingReviewPage /> : null}
      {pageId === 'operator-performance' ? <OperatorPerformancePage /> : null}
      {pageId === 'report-verification' ? <ReportVerificationPage /> : null}
    </React.Suspense>
  );
}

export function App() {
  const [pathname, setPathname] = React.useState(window.location.pathname);

  React.useEffect(() => {
    initConsoleDiagnostics();
    const handlePopState = () => _notifyPathnameChange();
    const handleHashChange = () => _notifyPathnameChange();
    _pathnames.add(setPathname);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      _pathnames.delete(setPathname);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
  }, [pathname]);

  const page = isReactPublicRoute(pathname) ? null : getReactAdminPage(pathname);
  const adminBody = page ? (
    <AppShell activeRoute={page.route.replace(/^\//, '')}>
      <UserAnalyticsProvider pageId={page.id}>
        <div className={adminPageRootClass}>
          <AdminPageContent pageId={page.id} />
        </div>
      </UserAnalyticsProvider>
    </AppShell>
  ) : null;

  return (
    <BrandingProvider>
      <DocumentTitle pathname={pathname} />
      {page ? (
        pageUsesDealerTree(page.id) ? (
          <DealerTreeProvider>{adminBody}</DealerTreeProvider>
        ) : (
          adminBody
        )
      ) : (
        <React.Suspense fallback={<PageSkeleton />}>
          <LoginPage />
        </React.Suspense>
      )}
      <Toaster />
    </BrandingProvider>
  );
}
